/**
 * Shared helpers for chat hooks (useChatStream + useAgentChat).
 * Extracted to avoid duplication.
 */

import type { ChatMessage, ChatWidget } from './useChatStream';

// ─── Widget detection ────────────────────────────

export function tryParseWidget(content: string): ChatWidget | undefined {
  const trimmed = content.trim();
  if (!trimmed.startsWith('{') && !trimmed.startsWith('```json')) return undefined;
  try {
    const raw = trimmed.startsWith('```json')
      ? trimmed.slice(7, trimmed.lastIndexOf('```')).trim()
      : trimmed;
    const parsed = JSON.parse(raw) as Record<string, unknown>;
    if (typeof parsed.type === 'string') return parsed as ChatWidget;
  } catch { /* not JSON */ }
  return undefined;
}

export function dbMsgToChat(m: Record<string, unknown>): ChatMessage {
  const content = String(m.content ?? '');
  return {
    id: String(m.id ?? Math.random()),
    role: m.sender_type === 'user' ? 'user' : m.sender_type === 'system' ? 'system' : 'assistant',
    content,
    createdAt: String(m.created_at ?? new Date().toISOString()),
    attachmentKeys: (m.data as { attachmentKeys?: string[] } | undefined)?.attachmentKeys,
    widgetData: tryParseWidget(content),
  };
}

export function mergeSyncedMessages(
  previousMessages: ChatMessage[],
  syncedMessages: ChatMessage[],
  streamId: string,
): ChatMessage[] {
  const streamMessage = previousMessages.find((message) => message.id === streamId);
  if (!streamMessage) return syncedMessages;

  const targetIndex = [...syncedMessages].map((message, index) => ({ message, index }))
    .reverse()
    .find((entry) => entry.message.role === 'assistant')?.index;

  if (targetIndex == null) return syncedMessages;

  const preservedAutonomousState = streamMessage.autonomousState
    ? {
      ...streamMessage.autonomousState,
      active: false,
    }
    : undefined;

  const hasEphemeralState = Boolean(
    streamMessage.agentInfo
    || streamMessage.thinkingSteps?.length
    || streamMessage.approvalRecommendation
    || preservedAutonomousState,
  );

  if (!hasEphemeralState) return syncedMessages;

  const mergedMessages = [...syncedMessages];
  const targetMessage = mergedMessages[targetIndex];
  if (!targetMessage) return syncedMessages;
  const baseMessage: ChatMessage = targetMessage;

  mergedMessages[targetIndex] = {
    ...baseMessage,
    ...(streamMessage.agentInfo ? { agentInfo: streamMessage.agentInfo } : {}),
    ...(streamMessage.thinkingSteps?.length ? { thinkingSteps: streamMessage.thinkingSteps } : {}),
    ...(streamMessage.approvalRecommendation ? { approvalRecommendation: streamMessage.approvalRecommendation } : {}),
    ...(preservedAutonomousState ? { autonomousState: preservedAutonomousState } : {}),
  };

  return mergedMessages;
}
